import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { casesService } from '../services/cases.service'
import { FuneralCase, CreateCaseData } from '../types'
import toast from 'react-hot-toast'

export function useCases(page = 1, limit = 10) {
  return useQuery({
    queryKey: ['cases', page, limit],
    queryFn: () => casesService.getCases(page, limit),
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

export function useCase(id: string) {
  return useQuery({
    queryKey: ['case', id],
    queryFn: () => casesService.getCase(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useDashboardStats() {
  return useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: () => casesService.getDashboardStats(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useCreateCaseWithWorkflow() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: casesService.createCaseWithWorkflow,
    onSuccess: (data) => {
      toast.success('Case created successfully!')
      queryClient.invalidateQueries({ queryKey: ['cases'] })
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create case')
    },
  })
}

export function useSearchCases() {
  return useMutation({
    mutationFn: (query: string) => casesService.searchCases(query),
  })
}

export function useCreateCase() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (caseData: CreateCaseData) => casesService.createCase(caseData),
    onSuccess: (newCase) => {
      toast.success('Case created successfully')
      queryClient.invalidateQueries({ queryKey: ['cases'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'stats'] })
      queryClient.setQueryData(['case', newCase.id], newCase)
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create case'
      toast.error(message)
    },
  })
}

export function useUpdateCase() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateCaseData> }) =>
      casesService.updateCase(id, data),
    onSuccess: (updatedCase) => {
      toast.success('Case updated successfully')
      queryClient.invalidateQueries({ queryKey: ['cases'] })
      queryClient.setQueryData(['case', updatedCase.id], updatedCase)
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update case'
      toast.error(message)
    },
  })
}

export function useUpdateCaseStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      casesService.updateCaseStatus(id, status),
    onSuccess: (updatedCase) => {
      toast.success('Case status updated successfully')
      queryClient.invalidateQueries({ queryKey: ['cases'] })
      queryClient.setQueryData(['case', updatedCase.id], updatedCase)
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'stats'] })
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update case status'
      toast.error(message)
    },
  })
}

export function useDeleteCase() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => casesService.deleteCase(id),
    onSuccess: (_, caseId) => {
      toast.success('Case deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['cases'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'stats'] })
      queryClient.removeQueries({ queryKey: ['case', caseId] })
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to delete case'
      toast.error(message)
    },
  })
}